import EntitlementWarning from '../../core/EntitlementWarning';
import Errored from '../../core/Errored';
import { ENTITLEMENTS } from '../../application-core/constants';
import WorkOffIcon from '@material-ui/icons/WorkOff';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import GS from '../../grafsync/grafsync';
import { makeCancelable, reverseSortByProp, sortByCreated } from '../../core/utils/utils';
import AddButton from '../../core/AddButton';
import CreateTaskDialog from './CreateTaskDialog';
import { Typography } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from '../style/gstheme';
import TaskViewerTaskDisplay from './TaskViewerTaskDisplay';
import { STATUS_PENDING } from '../utils/constants';
import PreviousTaskDisplay from './PreviousTaskDisplay';

const PENDING = STATUS_PENDING;
const MAX_TASKS = 20;

class TasksViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            infoLoading: false,
            showCreateTaskDialog: false,
            completedTasks: [],
            previousTasks: [],
            tasks: [],
            sortedTasks: [],
            showPreviousTasks: false,
            isAnAssignedUser: false,
            isErrored: undefined
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.getData();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
        return this.cancellable;
    }

    getData = async () => {
        try {
            GS.handle("get-data");
            if (!this.props.tasks || this.props.tasks.length === 0) {
                this.setState({ infoLoading: false, sortedTasks: [] });
                return;
            }
            await this.loadData(this.props.tasks, {}).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let isAnAssignedUser = false;
            const completedTasks = this.props.tasks.filter(task => {
                const data = GS.data(task, this.props.graph, false);
                if (data && data.assignedUser === this.props.currentUser) isAnAssignedUser = true;
                return data && "status" in data && data.status[0] !== PENDING;
            });
            const sortedTasks = sortByCreated([...this.props.tasks], this.props.graph, "created");
            this.setState({ completedTasks, infoLoading: false, sortedTasks, isAnAssignedUser });
            if (this.props.onUpdateCompletionCount) this.props.onUpdateCompletionCount(completedTasks);
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    createTask = (createdBy, assignedUser, text, lang) => {
        this.setState({ showCreateTaskDialog: false }, () => {
            this.props.onCreateTask(createdBy, assignedUser, text, lang);
        });
    };

    showCreateTask = () => this.setState({ showCreateTaskDialog: true });

    cancelCreateTask = () => this.setState({ showCreateTaskDialog: false });

    markAsCompleted = (task) => {
        let completedTasks = [...this.state.completedTasks];
        if (!completedTasks.includes(task)) completedTasks.push(task);
        this.setState({ completedTasks });
        if (this.props.onUpdateCompletionCount) this.props.onUpdateCompletionCount(completedTasks);
    };

    doSearch(payload) {
        this.cancellable = makeCancelable(GS.search(payload));
        return this.cancellable;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.tasks) {
            if (props.tasks.length === state.tasks.length) {
                const hasChanged = props.tasks.some(t => !state.tasks.includes(t));
                if (!hasChanged) return null;
            }
            return {
                infoLoading: true,
                tasks: props.tasks
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.infoLoading === true) {
            this.getData();
        }
    }

    loadPreviousTasks = async () => {
        try {
            GS.handle("load-previous-tasks");
            const results = await this.doSearch({
                search: [
                    {
                        predicate: "https://grafsync.graphifi.com/schema/gs/isTaskFor",
                        value: this.props.resourceIRI
                    },
                    {
                        predicate: "https://grafsync.graphifi.com/schema/gs/hasTask",
                        reverse: true,
                        value: this.props.resourceIRI,
                        invert: true
                    }, {
                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        value: "https://grafsync.graphifi.com/schema/gs/Task"
                    }
                ],
                graph: [this.props.graph],
                basicSort: true,
                pageSize: 50,
                sortOrder: "Descending",
                sort: ["http://purl.org/dc/terms/created"]
            }).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let resources = results.results || null;
            if (resources && resources.length > 0) {
                const previousTasks = reverseSortByProp([...resources], "created");
                this.setState({ previousTasks });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    onShowPreviousTasks = async (showPreviousTasks) => {
        if (showPreviousTasks) {
            if (this.state.previousTasks.length === 0) {
                await this.loadPreviousTasks();
            }
        }
        this.setState({ showPreviousTasks });
    };

    monitorUpdate = () => {
        if (!this.state.isErrored) {
            this.getData();
        }
    };

    render() {

        const { classes, graph, projectUsers, entitlements, defaultLang, currentUser, manager, visible, allowTaskCreation, disabled } = this.props;
        const { showCreateTaskDialog, showPreviousTasks, previousTasks, tasks, sortedTasks, isErrored, isAnAssignedUser } = this.state;

        if (isErrored) return <Errored dataTest="tv-error" data={isErrored} />;
        if (!visible) return null;

        const canEditTask = entitlements.includes(ENTITLEMENTS.EDIT_TASK) || entitlements.includes(ENTITLEMENTS.CREATE_TASK);
        const canAddTask = entitlements.includes(ENTITLEMENTS.CREATE_TASK);

        return (
            <div data-test="task-viewer-container">
                <div style={{ float: 'right', marginTop: 12 }}>
                    {allowTaskCreation && canAddTask && !(tasks && tasks.length >= MAX_TASKS) &&
                        <AddButton dataTest="add-task" onClick={this.showCreateTask} title="Add task" />
                    }
                    <span style={{ marginLeft: 12 }}>
                        <Tooltip placement="right" title='Show/hide previous tasks'>
                            <IconButton data-test="show-previous-tasks" size="small" onClick={() => this.onShowPreviousTasks(!showPreviousTasks)}>
                                <WorkOffIcon />
                            </IconButton>
                        </Tooltip>
                    </span>
                </div>
                <Typography className={classes.mainHeading} style={{ marginTop: 10 }}>Tasks {tasks && tasks.length > 0 &&
                    <span style={{ marginLeft: 12 }}>({tasks.length})</span>}</Typography>
                <div style={{ clear: 'both', minWidth: 600 }}>
                    {
                        isAnAssignedUser && !canEditTask &&
                        <div style={{ marginTop: 12, marginBottom: 12 }}>
                            <EntitlementWarning
                                compact={true}
                                border={true}
                                needAll={false}
                                message="You do not have permissions to mark tasks as done, nor to reject them."
                                requiredEntitlements={[
                                    ENTITLEMENTS.CREATE_TASK,
                                    ENTITLEMENTS.EDIT_TASK
                                ]} />
                        </div>
                    }
                    <Table data-test="timeline-viewer" style={{ maxWidth: '100%' }}>
                        {tasks && tasks.length > 0 &&
                            <TableHead>
                                <TableRow data-test="headers">
                                    {
                                        ["Assigned To", "Instructions", "Created By", "Date", "Done", "Rejected", "Reassign", ""]
                                            .map((col, index) =>
                                                <TableCell
                                                    key={index}
                                                    align={index === 6 ?
                                                        'right'
                                                        :
                                                        index === 4 || index === 5 ?
                                                            'center'
                                                            :
                                                            'left'}
                                                    component="th"
                                                    className={classes.tableHeading}>{col}</TableCell>
                                            )
                                    }
                                </TableRow>
                            </TableHead>
                        }
                        <TableBody data-test="current-task-list">
                            {sortedTasks.map((task) =>
                                <TaskViewerTaskDisplay
                                    suppressLoading={true}
                                    manager={manager}
                                    disabled={disabled}
                                    onTaskCompletion={this.markAsCompleted}
                                    updateEvent={this.monitorUpdate}
                                    currentUser={currentUser}
                                    projectUsers={projectUsers}
                                    entitlements={entitlements}
                                    defaultLang={defaultLang}
                                    key={task}
                                    id={task}
                                    graph={graph}
                                />
                            )}
                            {tasks.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={7} style={{ border: 'none', padding: 0 }}>
                                        <Typography data-test="no-outstanding-tasks" variant="body2" style={{ color: 'black' }}>No outstanding tasks.</Typography>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                        {showPreviousTasks &&
                            <TableBody data-test="previous-task-list">
                                <TableRow>
                                    <TableCell colSpan={7} style={{ padding: 0, border: 'none' }}>
                                        <Typography className={classes.mainHeading} style={{ marginTop: 24 }}>
                                            <span>Previous Tasks </span>
                                            {previousTasks && previousTasks.length > 0 &&
                                                <span style={{ marginLeft: 12 }}>({previousTasks.length})</span>}</Typography>
                                        {previousTasks.length === 50 &&
                                            <Typography data-test="fifty-recent" variant="caption">Showing the fifty most recent tasks.</Typography>
                                        }
                                    </TableCell>
                                </TableRow>
                                {previousTasks && previousTasks.length > 0 &&
                                    previousTasks.map(pt => <PreviousTaskDisplay
                                        key={pt.id}
                                        currentUser={currentUser}
                                        data={pt}
                                        projectUsers={projectUsers}
                                        entitlements={entitlements}
                                        defaultLang={defaultLang}
                                    />)
                                }
                                {previousTasks.length === 0 &&
                                    <TableRow>
                                        <TableCell colSpan={7} style={{ border: 'none', padding: 0 }}>
                                            <Typography data-test="no-previous-tasks" variant="body2" style={{ color: 'black' }}>No previous tasks.</Typography>
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        }
                    </Table>
                </div>
                {
                    showCreateTaskDialog &&
                    <CreateTaskDialog
                        createdBy={currentUser}
                        onCreateTask={this.createTask}
                        defaultLang={defaultLang}
                        projectUsers={projectUsers}
                        onCancel={this.cancelCreateTask}
                    />
                }
            </div >
        );
    }
}

TasksViewer.defaultProps = {
    tasks: [],
    visible: false,
    allowTaskCreation: false,
    disabled: false
};

TasksViewer.propTypes = {
    tasks: PropTypes.array.isRequired,
    visible: PropTypes.bool,
    resourceIRI: PropTypes.string,
    disabled: PropTypes.bool,
    allowTaskCreation: PropTypes.bool.isRequired,
    manager: PropTypes.string,
    graph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    currentUser: PropTypes.string.isRequired,
    onCreateTask: PropTypes.func.isRequired,
    onUpdateCompletionCount: PropTypes.func,
    entitlements: PropTypes.array,
    projectUsers: PropTypes.array.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(TasksViewer);